import { useEffect, useMemo, useState } from 'react';
import type {
  InternalProtocolSidecarLaneKind,
} from '@nadoutong/contracts';
import type {
  CodexAdapterBackend,
  CodexAdapterTransport,
  EgressAdapterConfig,
  EgressAdmission,
  EgressAdmissionBlacklist,
  EgressAdmissionLevel,
  EgressAdmissionMode,
  EgressCchFixup,
  EgressConfig,
  EgressEntrypointClass,
  EgressFirewallRef,
  EgressFixups,
  EgressLane,
  EgressLaneMode,
  EgressMatchedPartialIntegrity,
  EgressMatching,
  EgressSurfaceBucket,
  FirewallRulesetSummary,
  InternalProtocolSidecarCapabilities,
  InternalProtocolSidecarLaneCapability,
  OpenAIAdapterImagesEgress,
} from '@nadoutong/contracts';
import type {
  ArkVideoVendorAdapterId,
  GrokImageVendorAdapterId,
  GrokVideoVendorAdapterId,
} from '@nadoutong/contracts';
import {
  ARK_VIDEO_VENDOR_ADAPTER_IDS,
  EGRESS_ASSEMBLE_TARGET_LATEST,
  EgressConfigError,
  egressKindForLane,
  GROK_IMAGE_VENDOR_ADAPTER_IDS,
  GROK_VIDEO_VENDOR_ADAPTER_IDS,
  isEgressLane,
  parseEgressConfig,
} from '@nadoutong/contracts';
import { TriangleAlert } from 'lucide-react';
import { useT } from '../i18n/index.ts';
import { isOneOf } from '../lib/isOneOf.ts';
import { Checkbox, Chip, FormField, Input, Select, Textarea } from '../ui/index.ts';
import { EgressAdmissionBlacklistFields } from './EgressAdmissionBlacklistFields.tsx';
import { EgressFirewallFields, type FirewallArmingScope } from './EgressFirewallFields.tsx';

/**
 * Capabilities-driven typed egress config editor (epic protocol#473 R4).
 *
 * The selector value domains (lane / mode / assemble target) come from the
 * live `/v1/capabilities` publication relayed through ops-api; nothing is
 * hardcoded from a catalog copy. When capabilities are unavailable the
 * editor falls back to the degradation contract: a read-only view of the
 * current document with every write path disabled.
 *
 * Draft semantics: every axis keeps its last value while the operator
 * switches lanes or modes, so flipping a dropdown never wipes the rest of
 * the form; only the fields valid for the current lane/mode projection are
 * emitted, and the emitted document is re-validated with
 * `parseEgressConfig` before it reaches the caller.
 *
 * `matching` is only surfaced where it has runtime meaning: the gateway
 * consumes it exclusively on the claude-code lane (render strictness for
 * assemble, the sidecar-outage fallback for fixup). Passthrough has no
 * forwarding consumer, so the control is hidden there too (#1305) while
 * the draft retains the stored value. API-direct lanes keep their stored
 * value untouched but hide the control; the codex lane rejects `strict`
 * at parse time, so the projection pins it to the default `best-effort`
 * instead of letting the operator select a dead-end value.
 *
 * The admission section follows the same honesty rule (#1308): sub-fields
 * render only where a runtime consumer exists. The codex gate is binary
 * (armed unless mode is `direct`), so codex offers only the mode select
 * and the write-time model allowlist; claude-code under `direct` hides
 * the wire-policy fields (minimum level / matched-partial / plain
 * Anthropic groups) because the ingress policy is never sent then. The
 * draft retains hidden values across mode/lane switches — hiding a
 * control never rewrites the stored document.
 */

type Translate = ReturnType<typeof useT>;

const EGRESS_LANE_MODES = ['passthrough', 'fixup', 'assemble'] as const satisfies readonly EgressLaneMode[];
const EGRESS_ADMISSION_MODES = [
  'require-cc',
  'require-cc-black',
  'allow-anthropic',
  'allow-any-supported',
  'direct',
] as const satisfies readonly EgressAdmissionMode[];
/**
 * The admission modes the sidecar publishes and therefore vouches for. The
 * offered set is the intersection of these with `capabilities.admission.modes`
 * so the console can never offer a mode the sidecar would reject at its
 * recognize step.
 */
const CAPABILITY_PUBLISHED_ADMISSION_MODES = [
  'require-cc',
  'allow-anthropic',
  'allow-any-supported',
  'direct',
] as const satisfies readonly EgressAdmissionMode[];
/**
 * Router-only admission modes, admitted on a different basis than the ones
 * above (#1609).
 *
 * `require-cc-black` is evaluated entirely in the router and is deliberately
 * absent from the sidecar's wire vocabulary — `ccSidecarIngressMode`
 * (`packages/egress-runtime/src/admission-gates.ts`) emits it as
 * `allow-any-supported`, so a sidecar that has never heard the name still
 * serves an adopted profile. Intersecting it with a publication that by
 * design does not list it would make it permanently unselectable, so the
 * capability gate is not the right authority here: what vouches for these
 * modes is the shared strict parser, which runs on every emitted document
 * and rejects a mode on a lane that cannot express it.
 *
 * Each entry therefore names the lanes it parses on. `require-cc-black` is
 * claude-code only — `parseEgressConfig` refuses it elsewhere — and offering
 * it on codex would dead-end the whole projection, the same failure the codex
 * mode/matching pins above exist to avoid.
 */
const ROUTER_ONLY_ADMISSION_MODES: readonly {
  readonly mode: EgressAdmissionMode;
  readonly lanes: readonly EgressLane[];
}[] = [
  { mode: 'require-cc-black', lanes: ['claude-code'] },
];
const EGRESS_ADMISSION_LEVELS = [
  'reject',
  'partial',
  'standard',
  'unknown-new',
  'genuine',
] as const satisfies readonly EgressAdmissionLevel[];
const EGRESS_SURFACE_BUCKETS = [
  'model-validation',
  'title-gen',
  'agent-sdk',
  'subagent-isolated',
  'main-loop',
  'unknown',
] as const satisfies readonly EgressSurfaceBucket[];
const EGRESS_ENTRYPOINT_CLASSES = [
  'first-party-interactive',
  'first-party-managed',
  'agent-sdk',
  'integration',
  'internal',
] as const satisfies readonly EgressEntrypointClass[];
const CODEX_BACKENDS = ['api', 'oauth'] as const satisfies readonly CodexAdapterBackend[];
const OPENAI_IMAGES_EGRESS = ['via-responses', 'native'] as const satisfies
  readonly OpenAIAdapterImagesEgress[];
const CODEX_TRANSPORTS = ['http', 'wss'] as const satisfies readonly CodexAdapterTransport[];
const CCH_FIXUPS = ['ensure', 'recompute'] as const satisfies readonly EgressCchFixup[];

type TriState = '' | 'true' | 'false';

export interface EgressConfigDraft {
  readonly lane: EgressLane | null;
  readonly mode: EgressLaneMode | null;
  readonly target: string;
  readonly fixupFingerprint: boolean;
  readonly fixupCch: '' | EgressCchFixup;
  readonly matching: EgressMatching;
  readonly admissionEnabled: boolean;
  readonly admissionMode: EgressAdmissionMode;
  readonly admissionMinimumLevel: '' | EgressAdmissionLevel;
  readonly mpiEnabled: boolean;
  readonly mpiTargetLevel: '' | EgressAdmissionLevel;
  readonly mpiRequireFingerprintMatch: TriState;
  readonly mpiRequireManifestMatch: TriState;
  readonly mpiRequireNoConflicts: TriState;
  readonly mpiAllowedSurfaceBuckets: readonly EgressSurfaceBucket[];
  readonly mpiAllowedEntrypointClasses: readonly EgressEntrypointClass[];
  readonly mpiAllowedEntrypoints: string;
  readonly modelAllowlist: string;
  /**
   * The `require-cc-black` rule set, or `null` for "the key is absent"
   * (#1609).
   *
   * The distinction is kept rather than collapsed because the contract gives
   * absence and `{}` the same meaning but the runtime parity suite requires
   * that "neither may be normalized into the other" — a freshly flipped
   * profile stores no `blacklist`, one whose last rule was unticked stores
   * `{}`, and an unrelated edit must not rewrite either into the other.
   * Within the object, a rule's presence is its on state: unticking deletes
   * the key rather than writing a disabled marker.
   */
  readonly admissionBlacklist: EgressAdmissionBlacklist | null;
  /**
   * Admission sub-objects this editor has no control for, carried through a
   * round-trip untouched.
   *
   * Without this, `admissionFromDraft` rebuilds `admission` from the fields the
   * form models and silently drops everything else — so editing a profile for
   * an unrelated reason disarms whatever an operator had set by hand, while the
   * form still shows nothing wrong.
   *
   * The set is derived by subtraction rather than enumerated, so a future
   * admission key is preserved by default instead of quietly discarded until
   * someone remembers to add it here. It is empty as of #1609 — `blacklist`
   * was the last key in it and now has controls — which is exactly why the
   * subtraction has to stay: an enumerated version would have been deleted as
   * dead the moment it emptied, and the next key added to `EgressAdmission`
   * would be dropped silently.
   */
  readonly admissionPassThrough: Partial<EgressAdmission>;
  /**
   * Reference to a stored firewall ruleset, or `null` for "no reference"
   * (#1760).
   *
   * Held as the parsed contract object rather than as two flat fields because
   * `{ rulesetId }` and `{ rulesetId, version }` are different documents —
   * following `active_version` versus frozen at a version — and a flat
   * `firewallVersion: ''` would have to be re-derived into that distinction on
   * every projection. Rules are never held here: the contract refuses an
   * inlined chain, and the version history that makes a rule change reviewable
   * lives on the ruleset, not on this profile.
   */
  readonly firewall: EgressFirewallRef | null;
  /**
   * The stored `adapter` slot, held whole so an unmodelled lane adapter
   * survives a save (#1886).
   *
   * The same subtraction argument as `admissionPassThrough`, reached the
   * expensive way. This editor models `adapter.codex` and emitted `adapter` at
   * all only on the codex lane, so `adapter.openai` — the slot that decides
   * whether an images request leaves on the upstream's own Images endpoint —
   * was dropped by any save of any profile that carried one. And the effect
   * was not "revert to the other path": the openai lane's other path routes to
   * a backend that does not declare `openai-images`, so the upstream's whole
   * images traffic answers `PROTOCOL_NOT_SUPPORTED` until someone notices. An
   * unrelated edit to an unrelated field, and the SKU is dark.
   *
   * The codex branch below still writes its own half authoritatively; this is
   * only the floor it is spread over.
   */
  readonly adapterPassThrough: EgressAdapterConfig | null;
  /**
   * `adapter.openai.images` — which wire an images request leaves this
   * upstream on (#1886). Empty means "key absent", the stored default:
   * delivery rewritten through the Responses API. The rest of the openai
   * slot (`vendor`) stays pass-through; only this one key has a control.
   */
  readonly openaiImages: '' | OpenAIAdapterImagesEgress;
  /**
   * Opt-in OpenAI `prompt_cache_key` injection (#2473). Emitted only when
   * the draft is on the openai lane and this is true; otherwise the key is
   * absent, matching the contract's opt-in spelling.
   */
  readonly openaiPromptCacheKey: boolean;
  readonly codexBackend: CodexAdapterBackend;
  readonly codexTransport: CodexAdapterTransport;
  readonly codexStore: TriState;
  readonly codexBetaFeatures: string;
  readonly codexOriginatorOverride: string;
  readonly codexDropMaxOutputTokens: TriState;
  readonly codexBaseUrl: string;
  /** `adapter.arkVideo.vendor` — the task lane's mounting (#1883 second half). */
  readonly arkVendor: ArkVideoVendorAdapterId;
  /**
   * `adapter.arkVideo.deliveryQuantityCoefficient` as the operator typed it.
   * Empty means "omit" — the 1.0 identity pass-through. Held as text so a
   * half-typed value never round-trips through Number() mid-edit; the strict
   * parser refuses anything that is not a positive finite number.
   */
  readonly arkDeliveryQuantityCoefficient: string;
  /** `adapter.grokVideo.vendor` — the Grok video lane's mounting (#2138). */
  readonly grokVendor: GrokVideoVendorAdapterId;
  /**
   * `adapter.grokVideo.deliveryQuantityCoefficient` as the operator typed it.
   * Same empty-means-omit semantics as the ark coefficient.
   */
  readonly grokDeliveryQuantityCoefficient: string;
  /** `adapter.grokImage.vendor` — the Grok still-image lane's mounting (#2190). */
  readonly grokImageVendor: GrokImageVendorAdapterId;
}

const EMPTY_DRAFT: EgressConfigDraft = {
  lane: null,
  mode: null,
  target: '',
  fixupFingerprint: false,
  fixupCch: '',
  matching: 'best-effort',
  admissionEnabled: false,
  admissionMode: 'require-cc',
  admissionMinimumLevel: '',
  mpiEnabled: false,
  mpiTargetLevel: '',
  mpiRequireFingerprintMatch: '',
  mpiRequireManifestMatch: '',
  mpiRequireNoConflicts: '',
  mpiAllowedSurfaceBuckets: [],
  mpiAllowedEntrypointClasses: [],
  mpiAllowedEntrypoints: '',
  modelAllowlist: '',
  admissionBlacklist: null,
  admissionPassThrough: {},
  firewall: null,
  adapterPassThrough: null,
  openaiImages: '',
  openaiPromptCacheKey: false,
  codexBackend: 'api',
  codexTransport: 'http',
  codexStore: '',
  codexBetaFeatures: '',
  codexOriginatorOverride: '',
  codexDropMaxOutputTokens: '',
  codexBaseUrl: '',
  arkVendor: 'ucloud',
  arkDeliveryQuantityCoefficient: '',
  grokVendor: 'pool',
  grokDeliveryQuantityCoefficient: '',
  grokImageVendor: 'pool',
};

function triStateFromOptionalBoolean(value: boolean | undefined): TriState {
  if (value === undefined) return '';
  return value ? 'true' : 'false';
}

function optionalBooleanFromTriState(value: TriState): boolean | undefined {
  if (value === '') return undefined;
  return value === 'true';
}

function listToText(values: readonly string[] | undefined): string {
  return values === undefined ? '' : values.join('\n');
}

function textToList(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function draftFromEgressConfig(config: EgressConfig | null): EgressConfigDraft {
  if (config === null) {
    return EMPTY_DRAFT;
  }
  const admission = config.admission;
  const mpi = admission?.matchedPartialIntegrity;
  const codex = config.adapter?.codex;
  return {
    ...EMPTY_DRAFT,
    lane: config.lane,
    mode: config.mode,
    target: config.target ?? '',
    fixupFingerprint: config.fixups?.fingerprint === 'recompute',
    fixupCch: config.fixups?.cch ?? '',
    matching: config.matching,
    admissionEnabled: admission !== undefined,
    admissionMode: admission?.mode ?? EMPTY_DRAFT.admissionMode,
    admissionMinimumLevel: admission?.minimumLevel ?? '',
    mpiEnabled: mpi !== undefined,
    mpiTargetLevel: mpi?.targetLevel ?? '',
    mpiRequireFingerprintMatch: triStateFromOptionalBoolean(mpi?.requireFingerprintMatch),
    mpiRequireManifestMatch: triStateFromOptionalBoolean(mpi?.requireManifestMatch),
    mpiRequireNoConflicts: triStateFromOptionalBoolean(mpi?.requireNoConflicts),
    mpiAllowedSurfaceBuckets: mpi?.allowedSurfaceBuckets ?? [],
    mpiAllowedEntrypointClasses: mpi?.allowedEntrypointClasses ?? [],
    mpiAllowedEntrypoints: listToText(mpi?.allowedEntrypoints),
    modelAllowlist: listToText(admission?.modelAllowlist),
    admissionBlacklist: admission?.blacklist ?? null,
    admissionPassThrough: unmodelledAdmissionFields(admission),
    firewall: config.firewall ?? null,
    adapterPassThrough: config.adapter ?? null,
    openaiImages: config.adapter?.openai?.images ?? '',
    openaiPromptCacheKey: config.openaiPromptCacheKey === true,
    codexBackend: codex?.backend ?? EMPTY_DRAFT.codexBackend,
    codexTransport: codex?.transport ?? EMPTY_DRAFT.codexTransport,
    codexStore: triStateFromOptionalBoolean(codex?.store),
    codexBetaFeatures: listToText(codex?.betaFeatures),
    codexOriginatorOverride: codex?.originatorOverride ?? '',
    codexDropMaxOutputTokens: triStateFromOptionalBoolean(codex?.dropMaxOutputTokens),
    codexBaseUrl: codex?.baseURL ?? '',
    arkVendor: config.adapter?.arkVideo?.vendor ?? EMPTY_DRAFT.arkVendor,
    arkDeliveryQuantityCoefficient:
      config.adapter?.arkVideo?.deliveryQuantityCoefficient !== undefined
        ? String(config.adapter.arkVideo.deliveryQuantityCoefficient)
        : '',
    grokVendor: config.adapter?.grokVideo?.vendor ?? EMPTY_DRAFT.grokVendor,
    grokDeliveryQuantityCoefficient:
      config.adapter?.grokVideo?.deliveryQuantityCoefficient !== undefined
        ? String(config.adapter.grokVideo.deliveryQuantityCoefficient)
        : '',
    grokImageVendor: config.adapter?.grokImage?.vendor ?? EMPTY_DRAFT.grokImageVendor,
  };
}

function mpiFromDraft(draft: EgressConfigDraft): EgressMatchedPartialIntegrity {
  return {
    ...(draft.mpiTargetLevel !== '' ? { targetLevel: draft.mpiTargetLevel } : {}),
    ...(optionalBooleanFromTriState(draft.mpiRequireFingerprintMatch) !== undefined
      ? { requireFingerprintMatch: draft.mpiRequireFingerprintMatch === 'true' }
      : {}),
    ...(optionalBooleanFromTriState(draft.mpiRequireManifestMatch) !== undefined
      ? { requireManifestMatch: draft.mpiRequireManifestMatch === 'true' }
      : {}),
    ...(optionalBooleanFromTriState(draft.mpiRequireNoConflicts) !== undefined
      ? { requireNoConflicts: draft.mpiRequireNoConflicts === 'true' }
      : {}),
    ...(draft.mpiAllowedSurfaceBuckets.length > 0
      ? { allowedSurfaceBuckets: draft.mpiAllowedSurfaceBuckets }
      : {}),
    ...(draft.mpiAllowedEntrypointClasses.length > 0
      ? { allowedEntrypointClasses: draft.mpiAllowedEntrypointClasses }
      : {}),
    ...(textToList(draft.mpiAllowedEntrypoints).length > 0
      ? { allowedEntrypoints: textToList(draft.mpiAllowedEntrypoints) }
      : {}),
  };
}

/**
 * The admission fields this editor does NOT model, obtained by removing the
 * ones it does. Rest destructuring rather than a key allowlist: the fields the
 * form owns are named in exactly one place, and anything else is pass-through
 * by construction.
 */
function unmodelledAdmissionFields(
  admission: EgressAdmission | undefined,
): Partial<EgressAdmission> {
  if (admission === undefined) return {};
  const {
    mode: _mode,
    minimumLevel: _minimumLevel,
    matchedPartialIntegrity: _matchedPartialIntegrity,
    modelAllowlist: _modelAllowlist,
    blacklist: _blacklist,
    ...rest
  } = admission;
  return rest;
}

function admissionFromDraft(draft: EgressConfigDraft): EgressAdmission {
  const mpi = mpiFromDraft(draft);
  const modelAllowlist = textToList(draft.modelAllowlist);
  return {
    // Pass-through first: the modelled fields below are authoritative for the
    // keys the form owns, and must win over a stale copy of themselves.
    ...draft.admissionPassThrough,
    mode: draft.admissionMode,
    ...(draft.admissionMinimumLevel !== '' ? { minimumLevel: draft.admissionMinimumLevel } : {}),
    ...(draft.mpiEnabled && Object.keys(mpi).length > 0 ? { matchedPartialIntegrity: mpi } : {}),
    ...(modelAllowlist.length > 0 ? { modelAllowlist } : {}),
    // `blacklist` is only legal under `require-cc-black` — the parser rejects
    // it elsewhere — so the draft retains it across a mode switch the way
    // every other hidden axis is retained, and it is emitted only where it
    // parses. `null` means the key was absent and stays absent; `{}` means it
    // was present with nothing armed and stays present, because the runtime
    // parity suite requires that neither spelling be normalized into the
    // other.
    ...(draft.admissionMode === 'require-cc-black' && draft.admissionBlacklist !== null
      ? { blacklist: draft.admissionBlacklist }
      : {}),
  };
}

export type EgressConfigDraftResult =
  | { readonly ok: true; readonly config: EgressConfig }
  | { readonly ok: false; readonly error: string };

/**
 * Every `adapter` slot the contract defines, mapped to the lane it is legal
 * on.
 *
 * A `Record` over `keyof Required<EgressAdapterConfig>` rather than a list:
 * adding a slot to the contract without adding it here is a type error, which
 * is the property this table exists for. An enumeration that could silently
 * fall behind is exactly how `adapter.openai` came to be dropped by every save
 * (#1886).
 */
const ADAPTER_SLOT_LANE: {
  readonly [K in keyof Required<EgressAdapterConfig>]: EgressLane;
} = {
  codex: 'codex',
  openai: 'openai',
  arkVideo: 'ark_video_v1',
  grokVideo: 'grok_video_v1',
  grokImage: 'grok_image_v1',
};

/**
 * The stored `adapter` slot that is still legal on the lane being saved.
 *
 * Only the current lane's slot is carried. A lane SWITCH drops the previous
 * lane's slot, and that is required rather than lossy: `parseEgressConfig`
 * refuses a slot on the wrong lane, so carrying it across would dead-end the
 * whole projection and the operator could not save at all — the same reason
 * `matching` is pinned on a codex draft a few lines below.
 */
function carriedAdapterForLane(
  adapter: EgressAdapterConfig | null,
  lane: EgressLane,
): EgressAdapterConfig | undefined {
  if (adapter === null) return undefined;
  const carried: Record<string, unknown> = {};
  for (const [slot, slotLane] of Object.entries(ADAPTER_SLOT_LANE)) {
    const value = adapter[slot as keyof EgressAdapterConfig];
    if (slotLane === lane && value !== undefined) carried[slot] = value;
  }
  return Object.keys(carried).length === 0
    ? undefined
    : carried as EgressAdapterConfig;
}

/**
 * The openai lane's emitted `adapter`. The form owns exactly one key on the
 * slot — `openai.images` (#1886): a selection writes it, unset omits it, and
 * every other stored key (`vendor`, and whatever the contract grows) is
 * carried untouched. Presence spelling follows the blacklist convention: a
 * document that never carried the slot does not gain one from an unset
 * control, while clearing the slot's last key leaves `{ openai: {} }` rather
 * than deleting it — neither spelling is normalized into the other.
 */
function openaiAdapterFromDraft(
  images: '' | OpenAIAdapterImagesEgress,
  carried: EgressAdapterConfig | undefined,
): EgressAdapterConfig | undefined {
  const { images: _stale, ...passThrough } = carried?.openai ?? {};
  const openai = {
    ...passThrough,
    ...(images !== '' ? { images } : {}),
  };
  if (carried?.openai === undefined && Object.keys(openai).length === 0) return undefined;
  return { openai };
}

/**
 * Project the draft onto the current lane/mode axes and validate the
 * resulting document with the shared strict parser. Fields that are not
 * valid for the projection stay in the draft but are not emitted.
 */
export function egressConfigFromDraft(
  draft: EgressConfigDraft,
  targetDomain?: readonly string[],
): EgressConfigDraftResult {
  if (draft.lane === null) {
    return { ok: false, error: 'lane not selected' };
  }
  if (draft.mode === null) {
    return { ok: false, error: 'mode not selected' };
  }
  const kind = egressKindForLane(draft.lane);
  const fixups: EgressFixups = {
    ...(draft.fixupFingerprint ? { fingerprint: 'recompute' as const } : {}),
    ...(draft.fixupCch !== '' ? { cch: draft.fixupCch } : {}),
  };
  // The draft keeps the last chosen target across lane switches so a
  // round-trip restores it; it is only emitted while it belongs to the
  // current lane's published target domain (catalog ∪ "latest").
  const target = draft.target !== '' && (targetDomain === undefined || targetDomain.includes(draft.target))
    ? draft.target
    : '';
  const carriedAdapter = carriedAdapterForLane(draft.adapterPassThrough, draft.lane);
  // The openai lane's slot is folded with its one modelled key here rather
  // than in a branch below: unlike codex and ark its modelled key is
  // optional, so clearing it can leave nothing to emit at all.
  const emittedAdapter = draft.lane === 'openai'
    ? openaiAdapterFromDraft(draft.openaiImages, carriedAdapter)
    : carriedAdapter;
  const candidate = {
    kind,
    lane: draft.lane,
    mode: draft.mode,
    ...(draft.mode === 'assemble' && target !== '' ? { target } : {}),
    ...(draft.mode === 'fixup' && draft.lane === 'claude-code' && Object.keys(fixups).length > 0
      ? { fixups }
      : {}),
    // The codex lane rejects `strict` at parse time, so a matching value
    // retained from a claude-code draft is pinned to the default instead
    // of dead-ending the whole projection.
    matching: draft.lane === 'codex' ? 'best-effort' : draft.matching,
    ...(kind === 'cli-disguise' && draft.admissionEnabled
      ? { admission: admissionFromDraft(draft) }
      : {}),
    // Deliberately not gated on kind or lane, mirroring `parseEgressConfig`:
    // the firewall is protocol-agnostic and #1751 requires the codex and
    // api-direct lanes to carry one. Gating it here would silently drop a
    // stored reference the moment an operator edited a non-cli profile for an
    // unrelated reason.
    ...(draft.firewall !== null ? { firewall: draft.firewall } : {}),
    // Pass-through first, same shape and same reason as `admissionFromDraft`:
    // the lane branches below are authoritative for the slots this form owns
    // and must win over a stale copy of themselves, while a slot the form does
    // not model survives an edit instead of being dropped (#1886).
    ...(emittedAdapter !== undefined ? { adapter: emittedAdapter } : {}),
    ...(draft.lane === 'codex'
      ? {
        adapter: {
          ...carriedAdapter,
          codex: {
            backend: draft.codexBackend,
            transport: draft.codexTransport,
            ...(optionalBooleanFromTriState(draft.codexStore) !== undefined
              ? { store: draft.codexStore === 'true' }
              : {}),
            ...(textToList(draft.codexBetaFeatures).length > 0
              ? { betaFeatures: textToList(draft.codexBetaFeatures) }
              : {}),
            ...(draft.codexOriginatorOverride.trim().length > 0
              ? { originatorOverride: draft.codexOriginatorOverride.trim() }
              : {}),
            ...(optionalBooleanFromTriState(draft.codexDropMaxOutputTokens) !== undefined
              ? { dropMaxOutputTokens: draft.codexDropMaxOutputTokens === 'true' }
              : {}),
            ...(draft.codexBaseUrl.trim().length > 0 ? { baseURL: draft.codexBaseUrl.trim() } : {}),
          },
        },
      }
      : {}),
    ...(draft.lane === 'ark_video_v1'
      ? {
        adapter: {
          ...carriedAdapter,
          arkVideo: {
            // The pass-through floor first, same shape and same reason as the
            // codex branch: `parameterSpellings` has no form control yet
            // (today's fleet is all pass-through), so a stored map survives an
            // edit; the two modelled seats below are authoritative.
            ...carriedAdapter?.arkVideo,
            vendor: draft.arkVendor,
            ...(deliveryCoefficientFromDraft(draft.arkDeliveryQuantityCoefficient) !== undefined
              ? {
                deliveryQuantityCoefficient:
                  deliveryCoefficientFromDraft(draft.arkDeliveryQuantityCoefficient),
              }
              : {}),
          },
        },
      }
      : {}),
    ...(draft.lane === 'grok_video_v1'
      ? {
        adapter: {
          ...carriedAdapter,
          grokVideo: {
            ...carriedAdapter?.grokVideo,
            vendor: draft.grokVendor,
            ...(deliveryCoefficientFromDraft(draft.grokDeliveryQuantityCoefficient) !== undefined
              ? {
                deliveryQuantityCoefficient:
                  deliveryCoefficientFromDraft(draft.grokDeliveryQuantityCoefficient),
              }
              : {}),
          },
        },
      }
      : {}),
    ...(draft.lane === 'grok_image_v1'
      ? {
        adapter: {
          ...carriedAdapter,
          grokImage: {
            ...carriedAdapter?.grokImage,
            vendor: draft.grokImageVendor,
          },
        },
      }
      : {}),
    ...(draft.lane === 'openai' && draft.openaiPromptCacheKey === true
      ? { openaiPromptCacheKey: true }
      : {}),
  };
  try {
    return { ok: true, config: parseEgressConfig(candidate) };
  } catch (err) {
    if (err instanceof EgressConfigError) {
      return { ok: false, error: err.message };
    }
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * A delivery-quantity coefficient as the number the document should carry,
 * or `undefined` to omit the key (the 1.0 identity). A non-empty string that
 * does not parse is emitted as `NaN` ON PURPOSE: the shared strict parser
 * refuses it at the slot's `deliveryQuantityCoefficient` path with the real
 * constraint in the message, which is the same honesty path every other
 * malformed field takes — silently dropping the value would save a document
 * the operator believes carries a coefficient.
 */
function deliveryCoefficientFromDraft(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  return Number(trimmed);
}

/**
 * A lane option the editor can offer: the published sidecar shape for the
 * sidecar-vouched lanes, or a router-only entry for lanes the sidecar has
 * never heard of.
 */
export interface EditorLaneOption {
  readonly lane: EgressLane;
  readonly kind: InternalProtocolSidecarLaneKind;
  readonly mode: readonly EgressLaneMode[];
  readonly catalog?: NonNullable<InternalProtocolSidecarLaneCapability['catalog']>;
}

/**
 * Router-only lanes, admitted on the `ROUTER_ONLY_ADMISSION_MODES` basis
 * (#1883 second half): the ark task lane is evaluated entirely in the router
 * — its traffic never reaches the sidecar — so the capabilities publication
 * by design never lists it, and intersecting it with that publication would
 * make it permanently unselectable. What vouches for it is the shared strict
 * parser, which runs on every emitted document.
 */
const ROUTER_ONLY_LANES: readonly EditorLaneOption[] = [
  { lane: 'ark_video_v1', kind: 'api-direct', mode: ['passthrough'] },
  { lane: 'grok_video_v1', kind: 'api-direct', mode: ['passthrough'] },
  { lane: 'grok_image_v1', kind: 'api-direct', mode: ['passthrough'] },
];

/** Lanes published by capabilities that the typed config model understands. */
export function editableLaneCapabilities(
  capabilities: InternalProtocolSidecarCapabilities | null,
): readonly EditorLaneOption[] {
  if (capabilities === null) return [];
  return [
    ...capabilities.lanes.filter(
      (lane): lane is InternalProtocolSidecarLaneCapability & { lane: EgressLane } =>
        isEgressLane(lane.lane),
    ),
    ...ROUTER_ONLY_LANES,
  ];
}

export function egressLaneLabel(lane: EgressLane): string {
  switch (lane) {
    case 'claude-code':
      return 'Claude Code';
    case 'codex':
      return 'Codex';
    case 'anthropic':
      return 'Anthropic';
    case 'openai':
      return 'OpenAI';
    case 'gemini':
      return 'Gemini';
    case 'ark_video_v1':
      return 'Ark Video v1';
    case 'grok_video_v1':
      return 'Grok Video v1';
    case 'grok_image_v1':
      return 'Grok Image v1';
  }
}

export function egressConfigSummaryLabel(
  config: EgressConfig | null | undefined,
  t: Translate,
): string {
  if (config === null || config === undefined) {
    return t('egress_editor.summary.unconfigured');
  }
  const parts = [egressLaneLabel(config.lane), config.mode];
  if (config.target !== undefined) parts.push(config.target);
  return parts.join(' · ');
}

function modeOptionsForLane(
  laneCapability: EditorLaneOption | undefined,
): readonly EgressLaneMode[] {
  if (laneCapability === undefined) return [];
  const published = laneCapability.mode
    .filter((mode): mode is EgressLaneMode => isOneOf(mode, EGRESS_LANE_MODES));
  // The shared parser locks the codex lane to assemble; a drifted
  // capabilities publication must not surface dead-end modes (#1308) —
  // same convention as the codex matching pin in the projection.
  if (laneCapability.lane === 'codex') {
    return published.filter((mode) => mode === 'assemble');
  }
  return published;
}

function targetOptionsForLane(
  laneCapability: EditorLaneOption | undefined,
): readonly string[] {
  const catalog = laneCapability?.catalog ?? [];
  return [EGRESS_ASSEMBLE_TARGET_LATEST, ...catalog.map((entry) => entry.id)];
}

function modeHint(mode: EgressLaneMode | null, t: Translate): string | undefined {
  switch (mode) {
    case 'passthrough':
      return t('egress_editor.mode_hint.passthrough');
    case 'fixup':
      return t('egress_editor.mode_hint.fixup');
    case 'assemble':
      return t('egress_editor.mode_hint.assemble');
    default:
      return undefined;
  }
}

/**
 * `matching` semantics depend on the mode: render strictness under
 * assemble, the sidecar-outage fallback under fixup. Under passthrough it
 * has no forwarding effect (only the shape_fidelity observability label),
 * so the selector is not offered there (#1305) — the draft still retains
 * the stored value across mode round-trips.
 */
function matchingHint(mode: EgressLaneMode | null, t: Translate): string | undefined {
  switch (mode) {
    case 'fixup':
      return t('egress_editor.matching_hint.fixup');
    case 'assemble':
      return t('egress_editor.matching_hint.assemble');
    default:
      return undefined;
  }
}

/**
 * The two image delivery forms, named for an operator. The labels stay short
 * and carry the wire value; the field hint holds the full semantics —
 * `via-responses` and `native` are different deliverables, not two routes to
 * the same image (see `OpenAIAdapterConfig` in the contract).
 */
function openaiImagesOptionLabel(value: OpenAIAdapterImagesEgress, t: Translate): string {
  switch (value) {
    case 'via-responses':
      return t('egress_editor.openai_images_option.via_responses');
    case 'native':
      return t('egress_editor.openai_images_option.native');
  }
}

function admissionModeHint(mode: EgressAdmissionMode, lane: EgressLane | null, t: Translate): string {
  if (lane === 'codex') {
    // The codex gate (#1284) is binary: every non-direct mode arms the same
    // fail-closed Codex-CLI-shape check, so the per-mode identity semantics
    // below do not apply there.
    return mode === 'direct'
      ? t('egress_editor.admission_mode_hint.codex_direct')
      : t('egress_editor.admission_mode_hint.codex_armed');
  }
  switch (mode) {
    case 'require-cc':
      return t('egress_editor.admission_mode_hint.require_cc');
    case 'require-cc-black':
      return t('egress_editor.admission_mode_hint.require_cc_black');
    case 'allow-anthropic':
      return t('egress_editor.admission_mode_hint.allow_anthropic');
    case 'allow-any-supported':
      return t('egress_editor.admission_mode_hint.allow_any_supported');
    case 'direct':
      return t('egress_editor.admission_mode_hint.direct');
  }
}

/**
 * The admission modes to offer for a lane: the sidecar-published set,
 * intersected with what this console understands, followed by the router-only
 * modes this lane can express. See `ROUTER_ONLY_ADMISSION_MODES` for why the
 * second group is not subject to the intersection.
 */
export function admissionModeOptions(
  publishedModes: readonly string[],
  lane: EgressLane | null,
): readonly EgressAdmissionMode[] {
  return [
    ...publishedModes.filter((mode): mode is EgressAdmissionMode =>
      isOneOf(mode, CAPABILITY_PUBLISHED_ADMISSION_MODES)),
    ...ROUTER_ONLY_ADMISSION_MODES
      .filter((entry) => lane !== null && entry.lanes.includes(lane))
      .map((entry) => entry.mode),
  ];
}

/**
 * Whether the profile would be refused by a capability-pinned claude-code SKU
 * target.
 *
 * Mirrors `canonicalSkuEgressCompatible`
 * (`apps/gateway-worker/src/routing/sku-capability-pin-compatibility.ts`),
 * whose claude-code clause is `mode === 'direct'` and nothing else since
 * #1383. `require-cc-black` is compatible, exactly like the
 * `allow-any-supported` it is defined to be indistinguishable from; so is a
 * profile with admission switched off entirely, because an absent admission
 * resolves to `allow-any-supported`. A banner claiming either would be
 * refused would contradict the writes the gateway actually accepts — the
 * failure mode this function is extracted to make testable, rather than left
 * to a reader comparing two files.
 *
 * `admissionEnabled` is kept in the signature because the draft can be in that
 * state and callers pass it; it no longer changes the answer.
 */
export function ccSkuPinIncompatible(
  lane: EgressLane | null,
  admissionEnabled: boolean,
  mode: EgressAdmissionMode,
): boolean {
  return lane === 'claude-code' && admissionEnabled && mode === 'direct';
}

export function EgressConfigEditor({
  value,
  capabilities,
  disabled,
  idPrefix,
  firewallRulesets = [],
  firewallRulesetsUnavailable = false,
  firewallScope = 'profile',
  onChange,
}: {
  readonly value: EgressConfig | null;
  readonly capabilities: InternalProtocolSidecarCapabilities | null;
  readonly disabled: boolean;
  readonly idPrefix: string;
  /**
   * Rulesets offered by the ① selector. Optional and defaulted so hosts that
   * have no reason to load the list keep working; an empty list still renders
   * a stored reference as itself rather than as "not referenced".
   */
  readonly firewallRulesets?: readonly FirewallRulesetSummary[];
  readonly firewallRulesetsUnavailable?: boolean;
  readonly firewallScope?: FirewallArmingScope;
  readonly onChange: (result: EgressConfig | null) => void;
}): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState<EgressConfigDraft>(() => draftFromEgressConfig(value));
  const lanes = editableLaneCapabilities(capabilities);
  const laneCapability = draft.lane === null
    ? undefined
    : lanes.find((lane) => lane.lane === draft.lane);
  const targetDomain = useMemo(
    () => targetOptionsForLane(laneCapability),
    [laneCapability],
  );
  const result = useMemo(
    () => egressConfigFromDraft(draft, targetDomain),
    [draft, targetDomain],
  );

  useEffect(() => {
    // Intentionally keyed on the projected result only: `onChange` is a
    // host setter whose identity is not guaranteed stable across renders.
    onChange(result.ok ? result.config : null);
  }, [result]);

  if (capabilities === null) {
    return (
      <div
        className="flex flex-col gap-3 rounded-lg border border-warn/40 bg-warn-dim/40 p-3"
        data-testid={`${idPrefix}-egress-editor-degraded`}
      >
        <span className="inline-flex items-center gap-2 text-dense font-medium text-warn">
          <TriangleAlert aria-hidden="true" className="h-4 w-4" />
          {t('egress_editor.degraded.title')}
        </span>
        <p className="m-0 text-dense text-paper-mid">{t('egress_editor.degraded.body')}</p>
        <pre className="m-0 max-h-64 overflow-auto rounded bg-well p-3 font-mono text-label text-paper-mid">
          {value === null ? t('egress_editor.summary.unconfigured') : JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  }

  function patchDraft(patch: Partial<EgressConfigDraft>): void {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function handleLaneChange(nextLaneRaw: string): void {
    if (!isEgressLane(nextLaneRaw)) return;
    const nextCapability = lanes.find((lane) => lane.lane === nextLaneRaw);
    const nextModes = modeOptionsForLane(nextCapability);
    setDraft((prev) => {
      const mode = prev.mode !== null && nextModes.includes(prev.mode)
        ? prev.mode
        : nextModes[0] ?? null;
      // `target` is intentionally kept: the projection re-validates it
      // against the new lane's domain, so switching back restores it.
      return { ...prev, lane: nextLaneRaw, mode };
    });
  }

  const modes = modeOptionsForLane(laneCapability);
  const targets = targetOptionsForLane(laneCapability);
  const catalogEmpty = (laneCapability?.catalog ?? []).length === 0;
  const kind = draft.lane === null ? null : egressKindForLane(draft.lane);
  const admissionModes = admissionModeOptions(capabilities.admission.modes, draft.lane);
  const admissionLevels = capabilities.admission.levels
    .filter((level): level is EgressAdmissionLevel => isOneOf(level, EGRESS_ADMISSION_LEVELS));
  const mpiFields = new Set(capabilities.admission.matchedPartialIntegrity.fields);
  // Wire-policy fields (minimum level / matched-partial) reach the sidecar
  // only for claude-code with a non-direct mode; the codex gate is binary and
  // `direct` skips the ingress policy, so the controls hide everywhere else
  // (#1308). `require-cc-black` keeps them deliberately: it is emitted to the
  // sidecar as `allow-any-supported`, and `buildCcSidecarConfig`
  // (`packages/egress-runtime/src/sidecar-v1.ts`) puts a configured
  // minimumLevel / matchedPartialIntegrity into that ingress policy exactly as
  // it does for the incumbent — the runtime parity suite pins the live
  // profile's matchedPartialIntegrity under both modes. Hiding a control whose
  // value is still on the wire, and which this form still emits, would be the
  // opposite of the honesty rule above.
  const admissionWirePolicyVisible = draft.lane === 'claude-code' && draft.admissionMode !== 'direct';
  const mpiEmitted = draft.mpiEnabled && Object.keys(mpiFromDraft(draft)).length > 0;
  const minimumLevelEmitted = draft.admissionMinimumLevel !== '';
  const ccPinIncompatible = ccSkuPinIncompatible(
    draft.lane,
    draft.admissionEnabled,
    draft.admissionMode,
  );
  const blacklistVisible = draft.lane === 'claude-code' &&
    draft.admissionMode === 'require-cc-black';
  // Since #1311 each require-cc dimension defaults independently, so the
  // disclosure shows whenever at least one dimension is unset.
  const requireCcDefaultsActive = draft.lane === 'claude-code' &&
    draft.admissionEnabled &&
    draft.admissionMode === 'require-cc' &&
    (!minimumLevelEmitted || !mpiEmitted);

  return (
    <div className="flex flex-col gap-4" data-testid={`${idPrefix}-egress-editor`}>
      <section className="flex flex-col gap-3 rounded-lg bg-well p-3">
        <header className="flex flex-col gap-0.5">
          <h3 className="m-0 text-dense font-medium text-paper">
            {t('egress_editor.section.pipeline')}
          </h3>
          <p className="m-0 text-label text-paper-low">
            {t('egress_editor.section.pipeline_hint')}
          </p>
        </header>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label={t('egress_editor.lane')} fieldId={`${idPrefix}-egress-lane`}>
            <Select
              value={draft.lane ?? undefined}
              onValueChange={handleLaneChange}
              disabled={disabled}
              ariaLabel={t('egress_editor.lane')}
              placeholder={t('egress_editor.lane_placeholder')}
              testId={`${idPrefix}-egress-lane`}
              options={lanes.map((lane) => ({
                value: lane.lane,
                label: isEgressLane(lane.lane)
                  ? `${egressLaneLabel(lane.lane)} (${lane.kind})`
                  : lane.lane,
              }))}
            />
          </FormField>
          <FormField
            label={t('egress_editor.mode')}
            fieldId={`${idPrefix}-egress-mode`}
            hint={modeHint(draft.mode, t)}
          >
            <Select
              value={draft.mode ?? undefined}
              onValueChange={(mode) => {
                if (isOneOf(mode, EGRESS_LANE_MODES)) patchDraft({ mode });
              }}
              disabled={disabled || draft.lane === null}
              ariaLabel={t('egress_editor.mode')}
              placeholder={t('egress_editor.mode_placeholder')}
              testId={`${idPrefix}-egress-mode`}
              mono
              options={modes.map((mode) => ({ value: mode, label: mode }))}
            />
          </FormField>
          <FormField
            label={t('egress_editor.kind')}
            fieldId={`${idPrefix}-egress-kind`}
            hint={t('egress_editor.kind_hint')}
          >
            <div className="flex h-9 items-center">
              <Chip tone="neutral" lamp={false} className="font-mono">
                {kind ?? '—'}
              </Chip>
            </div>
          </FormField>
          {draft.lane === 'claude-code' && draft.mode !== 'passthrough' ? (
            <FormField
              label={t('egress_editor.matching')}
              fieldId={`${idPrefix}-egress-matching`}
              hint={matchingHint(draft.mode, t)}
            >
              <Select
                value={draft.matching}
                onValueChange={(matching) => {
                  if (matching === 'best-effort' || matching === 'strict') {
                    patchDraft({ matching });
                  }
                }}
                disabled={disabled}
                ariaLabel={t('egress_editor.matching')}
                testId={`${idPrefix}-egress-matching`}
                options={[
                  { value: 'best-effort', label: t('egress_editor.matching_option_default') },
                  { value: 'strict', label: 'strict' },
                ]}
              />
            </FormField>
          ) : null}
        </div>

        {draft.mode === 'assemble' ? (
          <FormField
            label={t('egress_editor.target')}
            fieldId={`${idPrefix}-egress-target`}
            hint={catalogEmpty ? t('egress_editor.target_catalog_empty') : t('egress_editor.target_hint')}
          >
            <Select
              // Remount per lane: Radix locks into uncontrolled mode once it
              // sees an undefined value, which would freeze the restored
              // target when the operator switches back to the original lane.
              key={draft.lane ?? 'unset'}
              value={draft.target !== '' && targets.includes(draft.target) ? draft.target : undefined}
              onValueChange={(target) => patchDraft({ target })}
              disabled={disabled}
              ariaLabel={t('egress_editor.target')}
              placeholder={t('egress_editor.target_placeholder')}
              testId={`${idPrefix}-egress-target`}
              mono
              options={targets.map((target) => ({ value: target, label: target }))}
            />
          </FormField>
        ) : null}

        {draft.mode === 'fixup' && draft.lane === 'claude-code' ? (
          <div className="flex flex-col gap-3 rounded-lg bg-card p-3">
            <h4 className="m-0 text-label font-medium text-paper-mid">
              {t('egress_editor.section.fixups')}
            </h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex h-9 items-center">
                <Checkbox
                  checked={draft.fixupFingerprint}
                  onCheckedChange={(checked) => patchDraft({ fixupFingerprint: checked })}
                  disabled={disabled}
                  label={t('egress_editor.fixup_fingerprint')}
                  testId={`${idPrefix}-egress-fixup-fingerprint`}
                />
              </div>
              <FormField label={t('egress_editor.fixup_cch')} fieldId={`${idPrefix}-egress-fixup-cch`}>
                <Select
                  value={draft.fixupCch === '' ? 'unset' : draft.fixupCch}
                  onValueChange={(cch) => {
                    patchDraft({ fixupCch: isOneOf(cch, CCH_FIXUPS) ? cch : '' });
                  }}
                  disabled={disabled}
                  ariaLabel={t('egress_editor.fixup_cch')}
                  options={[
                    { value: 'unset', label: t('egress_editor.option_unset') },
                    ...CCH_FIXUPS.map((cch) => ({ value: cch, label: cch })),
                  ]}
                />
              </FormField>
            </div>
          </div>
        ) : null}
      </section>

      <section
        className="flex flex-col gap-3 rounded-lg bg-well p-3"
        data-testid={`${idPrefix}-egress-inbound`}
      >
        <header className="flex flex-col gap-0.5">
          <h3 className="m-0 text-dense font-medium text-paper">
            {t('egress_editor.section.inbound')}
          </h3>
          <p className="m-0 text-label text-paper-low">
            {t('egress_editor.section.inbound_hint')}
          </p>
        </header>
        <EgressFirewallFields
          value={draft.firewall}
          rulesets={firewallRulesets}
          rulesetsUnavailable={firewallRulesetsUnavailable}
          scope={firewallScope}
          disabled={disabled}
          idPrefix={idPrefix}
          onChange={(firewall) => patchDraft({ firewall })}
        />
        {kind === 'cli-disguise' ? (
          // Only stated where both layers exist. On an api-direct lane there
          // is no ②, and a note explaining how the two interact would be
          // describing a block that is not on screen.
          <div
            className="flex flex-col gap-1 rounded-lg bg-card p-3"
            data-testid={`${idPrefix}-egress-layer-relation`}
          >
            <h4 className="m-0 text-label font-medium text-paper-mid">
              {t('egress_editor.layers.title')}
            </h4>
            <p className="m-0 text-label text-paper-low">{t('egress_editor.layers.order')}</p>
            <p className="m-0 text-label text-paper-low">
              {t('egress_editor.layers.allow_not_exempt')}
            </p>
            <p className="m-0 text-label text-paper-low">
              {t('egress_editor.layers.only_stricter')}
            </p>
            <p className="m-0 text-label text-paper-low">{t('egress_editor.layers.off_semantics')}</p>
          </div>
        ) : null}
      </section>

      {kind === 'cli-disguise' ? (
        <section className="flex flex-col gap-3 rounded-lg bg-well p-3">
          <header className="flex flex-col gap-0.5">
            <Checkbox
              checked={draft.admissionEnabled}
              onCheckedChange={(checked) => patchDraft({ admissionEnabled: checked })}
              disabled={disabled}
              label={t('egress_editor.admission_enabled')}
              testId={`${idPrefix}-egress-admission-enabled`}
            />
            <p className="m-0 text-label text-paper-low">
              {draft.lane === 'codex'
                ? t('egress_editor.admission_hint.codex')
                : t('egress_editor.admission_hint.claude_code')}
            </p>
          </header>
          {ccPinIncompatible ? (
            <p
              className="m-0 rounded bg-warn-dim/40 p-2 text-label text-warn"
              data-testid={`${idPrefix}-egress-admission-cc-pin-warning`}
            >
              {t('egress_editor.admission_cc_pin_warning')}
            </p>
          ) : null}
          {draft.admissionEnabled ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField
                  label={t('egress_editor.admission_mode')}
                  fieldId={`${idPrefix}-egress-admission-mode`}
                  hint={admissionModeHint(draft.admissionMode, draft.lane, t)}
                >
                  <Select
                    value={draft.admissionMode}
                    onValueChange={(mode) => {
                      if (isOneOf(mode, EGRESS_ADMISSION_MODES)) patchDraft({ admissionMode: mode });
                    }}
                    disabled={disabled}
                    ariaLabel={t('egress_editor.admission_mode')}
                    testId={`${idPrefix}-egress-admission-mode`}
                    mono
                    options={admissionModes.map((mode) => ({ value: mode, label: mode }))}
                  />
                </FormField>
                {admissionWirePolicyVisible ? (
                  <FormField
                    label={t('egress_editor.admission_minimum_level')}
                    fieldId={`${idPrefix}-egress-admission-minimum-level`}
                    hint={t('egress_editor.admission_minimum_level_hint')}
                  >
                    <Select
                      value={draft.admissionMinimumLevel === '' ? 'unset' : draft.admissionMinimumLevel}
                      onValueChange={(level) => {
                        patchDraft({
                          admissionMinimumLevel: isOneOf(level, EGRESS_ADMISSION_LEVELS) ? level : '',
                        });
                      }}
                      disabled={disabled}
                      ariaLabel={t('egress_editor.admission_minimum_level')}
                      options={[
                        { value: 'unset', label: t('egress_editor.option_unset') },
                        ...admissionLevels.map((level) => ({ value: level, label: level })),
                      ]}
                    />
                  </FormField>
                ) : null}
              </div>
              {requireCcDefaultsActive ? (
                <p
                  className="m-0 rounded bg-well p-2 text-label text-paper-mid"
                  data-testid={`${idPrefix}-egress-require-cc-default-note`}
                >
                  {t('egress_editor.require_cc_default_note')}
                </p>
              ) : null}
              {blacklistVisible ? (
                <EgressAdmissionBlacklistFields
                  value={draft.admissionBlacklist}
                  scope="profile"
                  disabled={disabled}
                  idPrefix={idPrefix}
                  onChange={(admissionBlacklist) => patchDraft({ admissionBlacklist })}
                />
              ) : null}
              <FormField
                label={t('egress_editor.model_allowlist')}
                fieldId={`${idPrefix}-egress-model-allowlist`}
                hint={draft.admissionMode === 'direct'
                  ? t('egress_editor.model_allowlist_hint.direct')
                  : t('egress_editor.model_allowlist_hint')}
              >
                <Textarea
                  id={`${idPrefix}-egress-model-allowlist`}
                  value={draft.modelAllowlist}
                  onChange={(event) => patchDraft({ modelAllowlist: event.target.value })}
                  disabled={disabled || !capabilities.admission.supportsModelAllowlist}
                  rows={2}
                  aria-label={t('egress_editor.model_allowlist')}
                />
              </FormField>
              {admissionWirePolicyVisible ? (
              <div className="flex flex-col gap-3 rounded-lg bg-card p-3">
                <div className="flex flex-col gap-0.5">
                  <Checkbox
                    checked={draft.mpiEnabled}
                    onCheckedChange={(checked) => patchDraft({ mpiEnabled: checked })}
                    disabled={disabled}
                    label={t('egress_editor.mpi_enabled')}
                    testId={`${idPrefix}-egress-mpi-enabled`}
                  />
                  <p className="m-0 text-label text-paper-low">
                    {t('egress_editor.mpi_hint')}
                  </p>
                </div>
                {draft.mpiEnabled && !mpiEmitted ? (
                  <p
                    className="m-0 rounded bg-warn-dim/40 p-2 text-label text-warn"
                    data-testid={`${idPrefix}-egress-mpi-empty-warning`}
                  >
                    {t('egress_editor.mpi_empty_warning')}
                  </p>
                ) : null}
                {draft.mpiEnabled ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {mpiFields.has('targetLevel') ? (
                      <FormField
                        label={t('egress_editor.mpi_target_level')}
                        fieldId={`${idPrefix}-egress-mpi-target-level`}
                      >
                        <Select
                          value={draft.mpiTargetLevel === '' ? 'unset' : draft.mpiTargetLevel}
                          onValueChange={(level) => {
                            patchDraft({
                              mpiTargetLevel: isOneOf(level, EGRESS_ADMISSION_LEVELS) ? level : '',
                            });
                          }}
                          disabled={disabled}
                          ariaLabel={t('egress_editor.mpi_target_level')}
                          options={[
                            { value: 'unset', label: t('egress_editor.option_unset') },
                            ...admissionLevels.map((level) => ({ value: level, label: level })),
                          ]}
                        />
                      </FormField>
                    ) : null}
                    {mpiFields.has('requireFingerprintMatch') ? (
                      <TriStateField
                        label={t('egress_editor.mpi_require_fingerprint_match')}
                        fieldId={`${idPrefix}-egress-mpi-require-fingerprint-match`}
                        value={draft.mpiRequireFingerprintMatch}
                        disabled={disabled}
                        onChange={(value) => patchDraft({ mpiRequireFingerprintMatch: value })}
                        t={t}
                      />
                    ) : null}
                    {mpiFields.has('requireManifestMatch') ? (
                      <TriStateField
                        label={t('egress_editor.mpi_require_manifest_match')}
                        fieldId={`${idPrefix}-egress-mpi-require-manifest-match`}
                        value={draft.mpiRequireManifestMatch}
                        disabled={disabled}
                        onChange={(value) => patchDraft({ mpiRequireManifestMatch: value })}
                        t={t}
                      />
                    ) : null}
                    {mpiFields.has('requireNoConflicts') ? (
                      <TriStateField
                        label={t('egress_editor.mpi_require_no_conflicts')}
                        fieldId={`${idPrefix}-egress-mpi-require-no-conflicts`}
                        value={draft.mpiRequireNoConflicts}
                        disabled={disabled}
                        onChange={(value) => patchDraft({ mpiRequireNoConflicts: value })}
                        t={t}
                      />
                    ) : null}
                    {mpiFields.has('allowedSurfaceBuckets') ? (
                      <fieldset className="m-0 border-0 p-0 sm:col-span-2">
                        <legend className="mb-1.5 text-label text-paper-mid">
                          {t('egress_editor.mpi_allowed_surface_buckets')}
                        </legend>
                        <div className="flex flex-wrap gap-3">
                          {EGRESS_SURFACE_BUCKETS.map((bucket) => (
                            <Checkbox
                              key={bucket}
                              checked={draft.mpiAllowedSurfaceBuckets.includes(bucket)}
                              onCheckedChange={(checked) => patchDraft({
                                mpiAllowedSurfaceBuckets: checked
                                  ? [...draft.mpiAllowedSurfaceBuckets, bucket]
                                  : draft.mpiAllowedSurfaceBuckets.filter((item) => item !== bucket),
                              })}
                              disabled={disabled}
                              label={bucket}
                            />
                          ))}
                        </div>
                      </fieldset>
                    ) : null}
                    {mpiFields.has('allowedEntrypointClasses') ? (
                      <fieldset className="m-0 border-0 p-0 sm:col-span-2">
                        <legend className="mb-1.5 text-label text-paper-mid">
                          {t('egress_editor.mpi_allowed_entrypoint_classes')}
                        </legend>
                        <div className="flex flex-wrap gap-3">
                          {EGRESS_ENTRYPOINT_CLASSES.map((entrypointClass) => (
                            <Checkbox
                              key={entrypointClass}
                              checked={draft.mpiAllowedEntrypointClasses.includes(entrypointClass)}
                              onCheckedChange={(checked) => patchDraft({
                                mpiAllowedEntrypointClasses: checked
                                  ? [...draft.mpiAllowedEntrypointClasses, entrypointClass]
                                  : draft.mpiAllowedEntrypointClasses.filter((item) => item !== entrypointClass),
                              })}
                              disabled={disabled}
                              label={entrypointClass}
                            />
                          ))}
                        </div>
                      </fieldset>
                    ) : null}
                    {mpiFields.has('allowedEntrypoints') ? (
                      <FormField
                        label={t('egress_editor.mpi_allowed_entrypoints')}
                        fieldId={`${idPrefix}-egress-mpi-allowed-entrypoints`}
                        hint={t('egress_editor.list_hint')}
                        className="sm:col-span-2"
                      >
                        <Textarea
                          id={`${idPrefix}-egress-mpi-allowed-entrypoints`}
                          value={draft.mpiAllowedEntrypoints}
                          onChange={(event) => patchDraft({ mpiAllowedEntrypoints: event.target.value })}
                          disabled={disabled}
                          rows={2}
                          aria-label={t('egress_editor.mpi_allowed_entrypoints')}
                        />
                      </FormField>
                    ) : null}
                  </div>
                ) : null}
              </div>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}

      {draft.lane === 'codex' ? (
        <section className="flex flex-col gap-3 rounded-lg bg-well p-3">
          <header className="flex flex-col gap-0.5">
            <h3 className="m-0 text-dense font-medium text-paper">
              {t('egress_editor.codex_adapter')}
            </h3>
            <p className="m-0 text-label text-paper-low">
              {t('egress_editor.codex_adapter_hint')}
            </p>
          </header>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <FormField label={t('egress_editor.codex_backend')} fieldId={`${idPrefix}-egress-codex-backend`}>
              <Select
                value={draft.codexBackend}
                onValueChange={(backend) => {
                  if (isOneOf(backend, CODEX_BACKENDS)) patchDraft({ codexBackend: backend });
                }}
                disabled={disabled}
                ariaLabel={t('egress_editor.codex_backend')}
                testId={`${idPrefix}-egress-codex-backend`}
                mono
                options={CODEX_BACKENDS.map((backend) => ({ value: backend, label: backend }))}
              />
            </FormField>
            <FormField label={t('egress_editor.codex_transport')} fieldId={`${idPrefix}-egress-codex-transport`}>
              <Select
                value={draft.codexTransport}
                onValueChange={(transport) => {
                  if (isOneOf(transport, CODEX_TRANSPORTS)) patchDraft({ codexTransport: transport });
                }}
                disabled={disabled}
                ariaLabel={t('egress_editor.codex_transport')}
                testId={`${idPrefix}-egress-codex-transport`}
                mono
                options={CODEX_TRANSPORTS.map((transport) => ({ value: transport, label: transport }))}
              />
            </FormField>
            <TriStateField
              label={t('egress_editor.codex_store')}
              fieldId={`${idPrefix}-egress-codex-store`}
              value={draft.codexStore}
              disabled={disabled}
              onChange={(value) => patchDraft({ codexStore: value })}
              t={t}
            />
            <TriStateField
              label={t('egress_editor.codex_drop_max_output_tokens')}
              fieldId={`${idPrefix}-egress-codex-drop-max-output-tokens`}
              value={draft.codexDropMaxOutputTokens}
              disabled={disabled}
              onChange={(value) => patchDraft({ codexDropMaxOutputTokens: value })}
              t={t}
            />
            <FormField
              label={t('egress_editor.codex_originator_override')}
              fieldId={`${idPrefix}-egress-codex-originator-override`}
            >
              <Input
                id={`${idPrefix}-egress-codex-originator-override`}
                type="text"
                mono
                value={draft.codexOriginatorOverride}
                onChange={(event) => patchDraft({ codexOriginatorOverride: event.target.value })}
                disabled={disabled}
                aria-label={t('egress_editor.codex_originator_override')}
              />
            </FormField>
            <FormField
              label={t('egress_editor.codex_base_url')}
              fieldId={`${idPrefix}-egress-codex-base-url`}
              className="sm:col-span-2"
            >
              <Input
                id={`${idPrefix}-egress-codex-base-url`}
                type="text"
                mono
                value={draft.codexBaseUrl}
                onChange={(event) => patchDraft({ codexBaseUrl: event.target.value })}
                disabled={disabled}
                aria-label={t('egress_editor.codex_base_url')}
              />
            </FormField>
            <FormField
              label={t('egress_editor.codex_beta_features')}
              fieldId={`${idPrefix}-egress-codex-beta-features`}
              hint={t('egress_editor.list_hint')}
            >
              <Textarea
                id={`${idPrefix}-egress-codex-beta-features`}
                value={draft.codexBetaFeatures}
                onChange={(event) => patchDraft({ codexBetaFeatures: event.target.value })}
                disabled={disabled}
                rows={2}
                aria-label={t('egress_editor.codex_beta_features')}
              />
            </FormField>
          </div>
        </section>
      ) : null}

      {draft.lane === 'openai' ? (
        <section className="flex flex-col gap-3 rounded-lg bg-well p-3">
          <header className="flex flex-col gap-0.5">
            <h3 className="m-0 text-dense font-medium text-paper">
              {t('egress_editor.openai_adapter')}
            </h3>
            <p className="m-0 text-label text-paper-low">
              {t('egress_editor.openai_adapter_hint')}
            </p>
          </header>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label={t('egress_editor.openai_images')}
              fieldId={`${idPrefix}-egress-openai-images`}
              hint={t('egress_editor.openai_images_hint')}
            >
              <Select
                value={draft.openaiImages === '' ? 'unset' : draft.openaiImages}
                onValueChange={(images) => {
                  patchDraft({ openaiImages: isOneOf(images, OPENAI_IMAGES_EGRESS) ? images : '' });
                }}
                disabled={disabled}
                ariaLabel={t('egress_editor.openai_images')}
                testId={`${idPrefix}-egress-openai-images`}
                options={[
                  { value: 'unset', label: t('egress_editor.openai_images_option_unset') },
                  ...OPENAI_IMAGES_EGRESS.map((value) => ({
                    value,
                    label: openaiImagesOptionLabel(value, t),
                  })),
                ]}
              />
            </FormField>
            <div className="flex h-9 items-center">
              <Checkbox
                checked={draft.openaiPromptCacheKey}
                onCheckedChange={(checked) => patchDraft({ openaiPromptCacheKey: checked })}
                disabled={disabled}
                label={t('egress_editor.openai_prompt_cache_key')}
                testId={`${idPrefix}-egress-openai-prompt-cache-key`}
              />
            </div>
          </div>
        </section>
      ) : null}

      {draft.lane === 'ark_video_v1' ? (
        <section className="flex flex-col gap-3 rounded-lg bg-well p-3">
          <header className="flex flex-col gap-0.5">
            <h3 className="m-0 text-dense font-medium text-paper">
              {t('egress_editor.ark_adapter')}
            </h3>
            <p className="m-0 text-label text-paper-low">
              {t('egress_editor.ark_adapter_hint')}
            </p>
          </header>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label={t('egress_editor.ark_vendor')}
              fieldId={`${idPrefix}-egress-ark-vendor`}
              hint={t('egress_editor.ark_vendor_hint')}
            >
              <Select
                value={draft.arkVendor}
                onValueChange={(vendor) => {
                  if (isOneOf(vendor, ARK_VIDEO_VENDOR_ADAPTER_IDS)) patchDraft({ arkVendor: vendor });
                }}
                disabled={disabled}
                ariaLabel={t('egress_editor.ark_vendor')}
                testId={`${idPrefix}-egress-ark-vendor`}
                mono
                options={ARK_VIDEO_VENDOR_ADAPTER_IDS.map((vendor) => ({
                  value: vendor,
                  label: vendor,
                }))}
              />
            </FormField>
            <FormField
              label={t('egress_editor.ark_coefficient')}
              fieldId={`${idPrefix}-egress-ark-coefficient`}
              hint={t('egress_editor.ark_coefficient_hint')}
            >
              <Input
                id={`${idPrefix}-egress-ark-coefficient`}
                type="text"
                mono
                value={draft.arkDeliveryQuantityCoefficient}
                onChange={(event) =>
                  patchDraft({ arkDeliveryQuantityCoefficient: event.target.value })}
                disabled={disabled}
                placeholder="1.0"
                aria-label={t('egress_editor.ark_coefficient')}
                data-testid={`${idPrefix}-egress-ark-coefficient`}
              />
            </FormField>
          </div>
          {draft.adapterPassThrough?.arkVideo?.parameterSpellings !== undefined
            && Object.keys(draft.adapterPassThrough.arkVideo.parameterSpellings).length > 0 ? (
            <div className="flex flex-col gap-2 rounded-lg bg-card p-3">
              <h4 className="m-0 text-label font-medium text-paper-mid">
                {t('egress_editor.ark_spellings')}
              </h4>
              <p className="m-0 text-label text-paper-low">
                {t('egress_editor.ark_spellings_hint')}
              </p>
              <pre className="m-0 max-h-40 overflow-auto rounded bg-well p-2 font-mono text-micro text-paper-mid">
                {JSON.stringify(draft.adapterPassThrough.arkVideo.parameterSpellings, null, 2)}
              </pre>
            </div>
          ) : (
            <p className="m-0 rounded bg-card p-2 text-label text-paper-low">
              {t('egress_editor.ark_spellings_empty')}
            </p>
          )}
        </section>
      ) : null}

      {draft.lane === 'grok_video_v1' ? (
        <section className="flex flex-col gap-3 rounded-lg bg-well p-3">
          <header className="flex flex-col gap-0.5">
            <h3 className="m-0 text-dense font-medium text-paper">
              {t('egress_editor.grok_adapter')}
            </h3>
            <p className="m-0 text-label text-paper-low">
              {t('egress_editor.grok_adapter_hint')}
            </p>
          </header>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label={t('egress_editor.grok_vendor')}
              fieldId={`${idPrefix}-egress-grok-vendor`}
              hint={t('egress_editor.grok_vendor_hint')}
            >
              <Select
                value={draft.grokVendor}
                onValueChange={(vendor) => {
                  if (isOneOf(vendor, GROK_VIDEO_VENDOR_ADAPTER_IDS)) patchDraft({ grokVendor: vendor });
                }}
                disabled={disabled}
                ariaLabel={t('egress_editor.grok_vendor')}
                testId={`${idPrefix}-egress-grok-vendor`}
                mono
                options={GROK_VIDEO_VENDOR_ADAPTER_IDS.map((vendor) => ({
                  value: vendor,
                  label: vendor,
                }))}
              />
            </FormField>
            <FormField
              label={t('egress_editor.grok_coefficient')}
              fieldId={`${idPrefix}-egress-grok-coefficient`}
              hint={t('egress_editor.grok_coefficient_hint')}
            >
              <Input
                id={`${idPrefix}-egress-grok-coefficient`}
                type="text"
                mono
                value={draft.grokDeliveryQuantityCoefficient}
                onChange={(event) =>
                  patchDraft({ grokDeliveryQuantityCoefficient: event.target.value })}
                disabled={disabled}
                placeholder="1.0"
                aria-label={t('egress_editor.grok_coefficient')}
                data-testid={`${idPrefix}-egress-grok-coefficient`}
              />
            </FormField>
          </div>
        </section>
      ) : null}

      {draft.lane === 'grok_image_v1' ? (
        <section className="flex flex-col gap-3 rounded-lg bg-well p-3">
          <header className="flex flex-col gap-0.5">
            <h3 className="m-0 text-dense font-medium text-paper">
              {t('egress_editor.grok_image_adapter')}
            </h3>
            <p className="m-0 text-label text-paper-low">
              {t('egress_editor.grok_image_adapter_hint')}
            </p>
          </header>
          <FormField
            label={t('egress_editor.grok_image_vendor')}
            fieldId={`${idPrefix}-egress-grok-image-vendor`}
            hint={t('egress_editor.grok_image_vendor_hint')}
          >
            <Select
              value={draft.grokImageVendor}
              onValueChange={(vendor) => {
                if (isOneOf(vendor, GROK_IMAGE_VENDOR_ADAPTER_IDS)) {
                  patchDraft({ grokImageVendor: vendor });
                }
              }}
              disabled={disabled}
              ariaLabel={t('egress_editor.grok_image_vendor')}
              testId={`${idPrefix}-egress-grok-image-vendor`}
              mono
              options={GROK_IMAGE_VENDOR_ADAPTER_IDS.map((vendor) => ({
                value: vendor,
                label: vendor,
              }))}
            />
          </FormField>
        </section>
      ) : null}

      {!result.ok && draft.lane !== null ? (
        <p className="m-0 text-dense text-warn" data-testid={`${idPrefix}-egress-editor-error`}>
          {draft.mode === 'assemble' && (draft.target === '' || !targets.includes(draft.target))
            ? t('egress_editor.error.target_required')
            : result.error}
        </p>
      ) : null}
    </div>
  );
}

function TriStateField({
  label,
  fieldId,
  value,
  disabled,
  onChange,
  t,
}: {
  readonly label: string;
  readonly fieldId: string;
  readonly value: TriState;
  readonly disabled: boolean;
  readonly onChange: (value: TriState) => void;
  readonly t: Translate;
}): JSX.Element {
  return (
    <FormField label={label} fieldId={fieldId}>
      <Select
        value={value === '' ? 'unset' : value}
        onValueChange={(next) => {
          onChange(next === 'true' ? 'true' : next === 'false' ? 'false' : '');
        }}
        disabled={disabled}
        ariaLabel={label}
        options={[
          { value: 'unset', label: t('egress_editor.option_unset') },
          { value: 'true', label: t('egress_editor.option_yes') },
          { value: 'false', label: t('egress_editor.option_no') },
        ]}
      />
    </FormField>
  );
}
